import { effect, Injectable, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { AppPayment, Payer } from '@models/api-calls/payment/payment.model';
import { MainSteps, PaymentSubSteps } from '@models/config/app-structure.model';
import { ApplicationService } from '@services/api-calls/application.service';
import { PaymentDataService } from '@services/api-calls/payment-data.service';
import { PaymentVerificationService } from '@services/api-calls/payment-verification.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import { GlobalStateService } from '@services/global-state.service';
import { PaymentDateOverrideService } from '@services/testing/payment-date-override.service';
import { DateUtil } from '@utils/date.util';
import {
    AchDisclaimerFormEnum,
    // IAPP2-1744: Additional person removed
    // AdditionalPersonFormEnum,
    AddressFormEnum,
    PaymentDateEnum,
    PaymentInformationFormEnum,
    PaymentMethodFormEnum,
    PaymentStatusEnum,
    PaymentTypeEnum,
    PaymentVerificationEnum,
    PayorIsEnum,
    PayorOtherFormEnum,
    PrimaryPaymentMethodFormEnum,
    SecondaryPaymentMethodFormEnum,
    // IAPP2-1744: SSD payment removed
    // SsdDateOptionsEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';

@Injectable({
    providedIn: 'root',
})
export class PaymentFormPopulationService {
    // Signals to trigger re-patching when dynamic controls are added
    // IAPP2-1744: Additional person removed
    // private additionalPersonGroupAdded = signal<number>(0);
    private futureDateControlAdded = signal<number>(0);
    // Stores mortgage_start_date for preselection when no payment data is saved yet
    private preselectedFutureDate = signal<string | null>(null);
    // IAPP2-1744: SSD payment removed
    // private futureDateSsdControlAdded = signal<number>(0);
    private primaryPayerOtherGroupAdded = signal<number>(0);
    private secondaryPayerOtherGroupAdded = signal<number>(0);
    private secondaryPaymentGroupAdded = signal<number>(0);

    constructor(
        private paymentDataService: PaymentDataService,
        private applicationFormService: ApplicationFormService,
        private applicationService: ApplicationService,
        private formRegistry: FormRegistryService,
        private globalStateService: GlobalStateService,
        private paymentVerificationService: PaymentVerificationService,
        private paymentDateOverride: PaymentDateOverrideService,
    ) {
        effect(() => {
            this.patchPaymentDataOnForm();
        });

        // IAPP2-1744: Additional person removed
        // effect(() => {
        //     this.patchPayerContactOnDynamicGroup();
        // });

        effect(() => {
            this.patchFutureDateOnDynamicControl();
        });

        // IAPP2-1744: SSD payment removed
        // effect(() => {
        //     this.patchFutureDateSsdOnDynamicControl();
        // });

        effect(() => {
            this.patchPrimaryPaymentMethodOnForm();
        });

        effect(() => {
            this.patchSecondaryPaymentMethodOnForm();
        });

        effect(() => {
            this.patchPrimaryPayerOtherOnDynamicGroup();
        });

        effect(() => {
            this.patchSecondaryPayerOtherOnDynamicGroup();
        });

        effect(() => {
            this.patchSecondaryPaymentGroupOnDynamicAdd();
        });

        effect(() => {
            this.patchAchDisclaimerOnForm();
        });

        // Lock Primary Payment Method when verified
        effect(() => {
            const isVerified = this.paymentVerificationService.getVerificationState(PaymentTypeEnum.PRIMARY)().verified;
            if (!isVerified) return;
            const primaryForm = this.getPrimaryPaymentMethodForm();
            if (primaryForm && primaryForm.enabled) {
                primaryForm.disable({ emitEvent: false });
            }
        });

        // Lock Secondary Payment Method when verified
        effect(() => {
            const isVerified = this.paymentVerificationService.getVerificationState(PaymentTypeEnum.SECONDARY)().verified;
            if (!isVerified) return;
            const secondaryForm = this.getSecondaryPaymentMethodForm();
            if (secondaryForm && secondaryForm.enabled) {
                secondaryForm.disable({ emitEvent: false });
            }
        });

        // Disable Primary Payment Method during verification, re-enable on failure
        effect(() => {
            const state = this.paymentVerificationService.getVerificationState(PaymentTypeEnum.PRIMARY)();
            const primaryForm = this.getPrimaryPaymentMethodForm();
            if (!primaryForm) return;
            if (state.verifying) {
                primaryForm.disable({ emitEvent: false });
            } else if (!state.verified && primaryForm.disabled) {
                primaryForm.enable({ emitEvent: false });
            }
        });

        // Disable Secondary Payment Method during verification, re-enable on failure
        effect(() => {
            const state = this.paymentVerificationService.getVerificationState(PaymentTypeEnum.SECONDARY)();
            const secondaryForm = this.getSecondaryPaymentMethodForm();
            if (!secondaryForm) return;
            if (state.verifying) {
                secondaryForm.disable({ emitEvent: false });
            } else if (!state.verified && secondaryForm.disabled) {
                secondaryForm.enable({ emitEvent: false });
            }
        });
    }

    // IAPP2-1744: Additional person removed
    // /**
    //  * Called by PaymentInformationComponent when ADDITIONAL_PERSON_GROUP is dynamically added.
    //  */
    // notifyAdditionalPersonGroupAdded(): void {
    //     this.additionalPersonGroupAdded.update((v) => v + 1);
    // }

    repatchPaymentDataFromServer(): void {
        const appPayment = this.paymentDataService.applicationPayment();
        const paymentInfoForm = this.getPaymentInfoForm();

        if (paymentInfoForm) {
            if (appPayment) {
                paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_FREQUENCY)?.patchValue(appPayment.payment_frequency);
                paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_DATE)?.patchValue(appPayment.payment_date_options);
                this.patchPaymentDateFields(appPayment, paymentInfoForm);
                this.applyMortgageStartDatePreselection(paymentInfoForm, appPayment);
            } else {
                paymentInfoForm.reset();
            }
        }

        const achDisclaimerForm = this.formRegistry.getFormGroup(MainSteps.PAYMENT, PaymentSubSteps.ACH_DISCLAIMER);
        if (achDisclaimerForm) {
            const control = achDisclaimerForm.get(AchDisclaimerFormEnum.ACH_PAYMENT_DISCLAIMER_ACCEPTED);
            if (control) {
                control.patchValue(appPayment?.has_payment_disclosures ?? null);
            }
        }
    }

    /**
     * Called by PaymentInformationComponent when FUTURE_PAYMENT_DATE control is dynamically added.
     */
    notifyFutureDateControlAdded(): void {
        this.futureDateControlAdded.update((v) => v + 1);
    }

    // IAPP2-1744: SSD payment removed
    // /**
    //  * Called by PaymentInformationComponent when FUTURE_PAYMENT_DATE_ON_DISABILITY control is dynamically added.
    //  */
    // notifyFutureDateSsdControlAdded(): void {
    //     this.futureDateSsdControlAdded.update((v) => v + 1);
    // }

    /**
     * Called by PrimaryPaymentMethodComponent when PAYOR_OTHER_GROUP is dynamically added.
     */
    notifyPrimaryPayerOtherGroupAdded(): void {
        this.primaryPayerOtherGroupAdded.update((v) => v + 1);
    }

    /**
     * Called by SecondaryPaymentMethodComponent when PAYOR_OTHER_GROUP is dynamically added.
     */
    notifySecondaryPayerOtherGroupAdded(): void {
        this.secondaryPayerOtherGroupAdded.update((v) => v + 1);
    }

    /**
     * Called by SecondaryPaymentMethodComponent when PAYMENT_METHOD_GROUP is dynamically added (user selects "Yes").
     */
    notifySecondaryPaymentGroupAdded(): void {
        this.secondaryPaymentGroupAdded.update((v) => v + 1);
    }

    // IAPP2-1744: SSD and additional person removed - simplified
    private patchPaymentDataOnForm(): void {
        const appPayment = this.paymentDataService.applicationPayment();

        const paymentInfoForm = this.getPaymentInfoForm();
        if (!paymentInfoForm) return;

        if (appPayment) {
            // Patch payment frequency
            paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_FREQUENCY)?.patchValue(appPayment.payment_frequency);

            // Patch payment date
            paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_DATE)?.patchValue(appPayment.payment_date_options);

            // IAPP2-1744: SSD payment removed
            // paymentInfoForm.get(PaymentInformationFormEnum.SOCIAL_SECURITY_AND_DISABILITY)?.patchValue(appPayment.payment_ssd);

            // Patch date fields based on payment_date_options
            this.patchPaymentDateFields(appPayment, paymentInfoForm);

            // IAPP2-1744: Additional person removed
            // if (appPayment.payer_contact_id) {
            //     const insuredClientId = this.applicationService.application()?.insured_client_id;
            //     const designateAdditionalPerson =
            //         appPayment.payer_contact_id === insuredClientId ? YesNoOptionsEnum.NO : YesNoOptionsEnum.YES;
            //     paymentInfoForm.get(PaymentInformationFormEnum.DESIGNATE_ADDITIONAL_PERSON)?.patchValue(designateAdditionalPerson);
            // }
        }

        // Apply mortgage_start_date preselection even when app_payment doesn't exist yet —
        // preselection only needs uw_at + mortgage_start_date from the application, not payment data
        this.applyMortgageStartDatePreselection(paymentInfoForm, appPayment);
    }

    private applyMortgageStartDatePreselection(
        paymentInfoForm: ReturnType<typeof this.getPaymentInfoForm>,
        appPayment: AppPayment | null,
    ): void {
        if (!paymentInfoForm) {
            return;
        }
        if (appPayment?.initial_payment_at) {
            return;
        }

        const application = this.applicationService.application();
        const uwAtOverride = this.paymentDateOverride.uwAtOverride();
        const mortgageOverride = this.paymentDateOverride.mortgageStartDateOverride();
        const uwAt = uwAtOverride ?? application?.app_emc.uw_at;
        const mortgageStartDate = mortgageOverride ?? application?.insured_client?.lead_data?.mortgage_start_date;

        if (!uwAt || !mortgageStartDate) {
            return;
        }

        const uwAtDate = new Date(uwAt);
        const mortgageDateRaw = new Date(mortgageStartDate);

        if (isNaN(uwAtDate.getTime()) || isNaN(mortgageDateRaw.getTime())) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const mortgageDate = new Date(mortgageDateRaw);
        mortgageDate.setHours(0, 0, 0, 0);

        if (mortgageDate < today) {
            return;
        }

        const uwAtDay = new Date(uwAtDate);
        uwAtDay.setHours(0, 0, 0, 0);
        const maxDate = new Date(uwAtDate);
        maxDate.setDate(maxDate.getDate() + 45);
        maxDate.setHours(23, 59, 59, 999);

        if (mortgageDate < uwAtDay || mortgageDate > maxDate) {
            return;
        }

        const dayOfMonth = mortgageDate.getDate();
        if (dayOfMonth >= 29) {
            return;
        }

        const mortgageDateStr = DateUtil.toServer(mortgageDate)!;
        const todayStr = DateUtil.toServer(today)!;

        if (mortgageDateStr === todayStr) {
            paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_DATE)?.patchValue(PaymentDateEnum.AT_TIME_OF_SALE);
        } else {
            paymentInfoForm.get(PaymentInformationFormEnum.PAYMENT_DATE)?.patchValue(PaymentDateEnum.FUTURE_PAYMENT_DATE);
            this.preselectedFutureDate.set(mortgageDateStr);
        }
    }

    // IAPP2-1744: Additional person removed
    // private patchPayerContactOnDynamicGroup(): void {
    //     // Read signal to establish reactive dependency
    //     this.additionalPersonGroupAdded();

    //     const appPayment = this.paymentDataService.applicationPayment();
    //     if (!appPayment?.payer_contact) return;

    //     const paymentInfoForm = this.getPaymentInfoForm();
    //     if (!paymentInfoForm) return;

    //     const additionalPersonGroup = paymentInfoForm.get(PaymentInformationFormEnum.ADDITIONAL_PERSON_GROUP);
    //     if (!additionalPersonGroup) return;

    //     const payerContact = appPayment.payer_contact;

    //     // Only patch if the fields are empty (avoid overwriting user edits)
    //     const firstName = additionalPersonGroup.get(AdditionalPersonFormEnum.FIRST_NAME);
    //     if (firstName && !firstName.value && payerContact.first_name) {
    //         firstName.patchValue(payerContact.first_name);
    //     }
    //     // ... rest of method removed
    // }

    // IAPP2-1744: SSD payment removed - simplified
    private patchFutureDateOnDynamicControl(): void {
        // Read signal to establish reactive dependency
        this.futureDateControlAdded();

        const appPayment = this.paymentDataService.applicationPayment();
        const hasSavedFutureDate = appPayment?.payment_date_options === PaymentDateEnum.FUTURE_PAYMENT_DATE;
        const preselected = this.preselectedFutureDate();
        const hasPreselectedFutureDate = !!preselected;
        if (!hasSavedFutureDate && !hasPreselectedFutureDate) {
            return;
        }

        // Use saved date first, then fall back to mortgage_start_date preselection
        const dateToSet = appPayment?.initial_payment_at ?? preselected;
        if (!dateToSet) {
            return;
        }

        const paymentInfoForm = this.getPaymentInfoForm();
        if (!paymentInfoForm) {
            return;
        }

        const futureDateControl = paymentInfoForm.get(PaymentInformationFormEnum.FUTURE_PAYMENT_DATE);
        if (!futureDateControl) {
            return;
        }

        // Only patch if empty (avoid overwriting user edits)
        if (!futureDateControl.value) {
            futureDateControl.patchValue(dateToSet as unknown as string);
        }
    }

    // IAPP2-1744: SSD payment removed
    // private patchFutureDateSsdOnDynamicControl(): void {
    //     // Read signal to establish reactive dependency
    //     this.futureDateSsdControlAdded();

    //     const appPayment = this.paymentDataService.applicationPayment();
    //     if (!appPayment?.initial_payment_day) return;
    //     if (appPayment.payment_date_options !== PaymentDateEnum.FUTURE_PAYMENT_DATE) return;
    //     if (!appPayment.payment_ssd) return; // Only for SSD

    //     const paymentInfoForm = this.getPaymentInfoForm();
    //     if (!paymentInfoForm) return;

    //     const ssdControl = paymentInfoForm.get(PaymentInformationFormEnum.FUTURE_PAYMENT_DATE_ON_DISABILITY);
    //     if (!ssdControl) return;

    //     // Only patch if empty (avoid overwriting user edits)
    //     if (!ssdControl.value) {
    //         const ssdOption = this.mapToSsdDateOption(appPayment.initial_payment_day, appPayment.initial_payment_day_details);
    //         ssdControl.patchValue(ssdOption);
    //     }
    // }

    private getPaymentInfoForm() {
        const applicationForm = this.applicationFormService.applicationForm;
        return applicationForm.get(MainSteps.PAYMENT)?.get(PaymentSubSteps.PAYMENT_INFORMATION);
    }

    // IAPP2-1744: SSD payment removed - simplified
    private patchPaymentDateFields(appPayment: AppPayment, paymentInfoForm: ReturnType<typeof this.getPaymentInfoForm>): void {
        if (!paymentInfoForm) return;

        const paymentDateOption = appPayment.payment_date_options;

        if (paymentDateOption === PaymentDateEnum.FUTURE_PAYMENT_DATE) {
            // Regular future date - use initial_payment_at (Date object for PrimeNG calendar)
            const futureDateControl = paymentInfoForm.get(PaymentInformationFormEnum.FUTURE_PAYMENT_DATE);
            if (futureDateControl && appPayment.initial_payment_at) {
                futureDateControl.patchValue(appPayment.initial_payment_at as unknown as string);
            }
        }

        // Patch recurring payment date
        paymentInfoForm.get(PaymentInformationFormEnum.RECURRING_PAYMENT_DATE)?.patchValue(appPayment.draw_recurring_day);
    }

    // IAPP2-1744: SSD payment removed
    // private mapToSsdDateOption(day: number | null, details: string | null): SsdDateOptionsEnum | null {
    //     if (day === null) return null;

    //     if (details === 'wednesday') {
    //         switch (day) {
    //             case 2:
    //                 return SsdDateOptionsEnum.SECOND_WEDNESDAY;
    //             case 3:
    //                 return SsdDateOptionsEnum.THIRD_WEDNESDAY;
    //             case 4:
    //                 return SsdDateOptionsEnum.FOURTH_WEDNESDAY;
    //         }
    //     } else {
    //         switch (day) {
    //             case 1:
    //                 return SsdDateOptionsEnum.FIRST_DAY;
    //             case 3:
    //                 return SsdDateOptionsEnum.THIRD_DAY;
    //         }
    //     }

    //     return null;
    // }

    private patchPrimaryPaymentMethodOnForm(): void {
        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment) return;

        const primaryPaymentMethodForm = this.getPrimaryPaymentMethodForm();
        if (!primaryPaymentMethodForm) return;

        const paymentMethodGroup = primaryPaymentMethodForm.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        if (!paymentMethodGroup) return;

        const insuredClientId = this.applicationService.application()?.insured_client_id;
        const primaryPayerId = appPayment.primary_payer_id;

        // Determine if payer is proposed insured or other
        if (primaryPayerId === 0 || (primaryPayerId && primaryPayerId !== insuredClientId)) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYOR_IS)?.patchValue(PayorIsEnum.OTHER);
        } else if (primaryPayerId && primaryPayerId === insuredClientId) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYOR_IS)?.patchValue(PayorIsEnum.PROPOSED_INSURED);
        }

        // Patch payment method type (ACH or Credit Card)
        if (appPayment.primary_payment_method) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE)?.patchValue(appPayment.primary_payment_method);
        }

        // If already verified, set VALID control to YES
        const isVerified =
            appPayment.primary_payment_status === PaymentStatusEnum.COMPLETED &&
            appPayment.primary_payment_verification === PaymentVerificationEnum.SUCCESS;

        if (isVerified) {
            primaryPaymentMethodForm.get(PaymentMethodFormEnum.VALID)?.patchValue(YesNoOptionsEnum.YES);
        }
    }

    private patchSecondaryPaymentMethodOnForm(): void {
        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment) return;

        const secondaryPaymentMethodForm = this.getSecondaryPaymentMethodForm();
        if (!secondaryPaymentMethodForm) return;

        // Patch USE_SECONDARY_PAYMENT strictly from the backend-stored answer.
        // app_payment.secondary_payment_selected: 1 = YES, 0 = NO, null = unanswered.
        // Leaves the form at its null default when unanswered so we don't pre-select.
        const useSecondaryControl = secondaryPaymentMethodForm.get(SecondaryPaymentMethodFormEnum.USE_SECONDARY_PAYMENT);
        if (appPayment.secondary_payment_selected === 1) {
            useSecondaryControl?.patchValue(YesNoOptionsEnum.YES);
        } else if (appPayment.secondary_payment_selected === 0) {
            useSecondaryControl?.patchValue(YesNoOptionsEnum.NO);
        }

        // The PAYMENT_METHOD_GROUP will be added dynamically when "Yes" is selected,
        // and patchSecondaryPaymentGroupOnDynamicAdd will handle patching it
    }

    private patchSecondaryPaymentGroupOnDynamicAdd(): void {
        // Read signal to establish reactive dependency
        this.secondaryPaymentGroupAdded();

        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment?.secondary_payer_id) return;

        const secondaryPaymentMethodForm = this.getSecondaryPaymentMethodForm();
        if (!secondaryPaymentMethodForm) return;

        const paymentMethodGroup = secondaryPaymentMethodForm.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        if (!paymentMethodGroup) return;

        const insuredClientId = this.applicationService.application()?.insured_client_id;
        const secondaryPayerId = appPayment.secondary_payer_id;

        // Determine if payer is proposed insured or other
        if (secondaryPayerId === 0 || (secondaryPayerId && secondaryPayerId !== insuredClientId)) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYOR_IS)?.patchValue(PayorIsEnum.OTHER);
        } else if (secondaryPayerId && secondaryPayerId === insuredClientId) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYOR_IS)?.patchValue(PayorIsEnum.PROPOSED_INSURED);
        }

        // Patch payment method type (ACH or Credit Card)
        if (appPayment.secondary_payment_method) {
            paymentMethodGroup.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE)?.patchValue(appPayment.secondary_payment_method);
        }

        // If already verified, set VALID control to YES
        const isVerified =
            appPayment.secondary_payment_status === PaymentStatusEnum.COMPLETED &&
            appPayment.secondary_payment_verification === PaymentVerificationEnum.SUCCESS;

        if (isVerified) {
            secondaryPaymentMethodForm.get(PaymentMethodFormEnum.VALID)?.patchValue(YesNoOptionsEnum.YES);
        }
    }

    private patchPrimaryPayerOtherOnDynamicGroup(): void {
        // Read signal to establish reactive dependency
        this.primaryPayerOtherGroupAdded();

        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment?.primary_payer) return;

        const insuredClientId = this.applicationService.application()?.insured_client_id;
        if (appPayment.primary_payer_id === insuredClientId) return; // Only for "Other" payer

        const primaryPaymentMethodForm = this.getPrimaryPaymentMethodForm();
        if (!primaryPaymentMethodForm) return;

        const paymentMethodGroup = primaryPaymentMethodForm.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const payorOtherGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_OTHER_GROUP);
        if (!payorOtherGroup) return;

        this.patchPayerOnForm(appPayment.primary_payer, payorOtherGroup);
    }

    private patchSecondaryPayerOtherOnDynamicGroup(): void {
        // Read signal to establish reactive dependency
        this.secondaryPayerOtherGroupAdded();

        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment?.secondary_payer) return;

        const insuredClientId = this.applicationService.application()?.insured_client_id;
        if (appPayment.secondary_payer_id === insuredClientId) return; // Only for "Other" payer

        const secondaryPaymentMethodForm = this.getSecondaryPaymentMethodForm();
        if (!secondaryPaymentMethodForm) return;

        const paymentMethodGroup = secondaryPaymentMethodForm.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const payorOtherGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_OTHER_GROUP);
        if (!payorOtherGroup) return;

        this.patchPayerOnForm(appPayment.secondary_payer, payorOtherGroup);
    }

    private patchPayerOnForm(payer: Payer, payorOtherGroup: AbstractControl): void {
        // Only patch if fields are empty (avoid overwriting user edits)
        const firstName = payorOtherGroup.get(PayorOtherFormEnum.FIRST_NAME);
        if (firstName && !firstName.value && payer.first_name) {
            firstName.patchValue(payer.first_name);
        }

        const middleName = payorOtherGroup.get(PayorOtherFormEnum.MIDDLE_NAME);
        if (middleName && !middleName.value && payer.middle_name) {
            middleName.patchValue(payer.middle_name);
        }

        const lastName = payorOtherGroup.get(PayorOtherFormEnum.LAST_NAME);
        if (lastName && !lastName.value && payer.last_name) {
            lastName.patchValue(payer.last_name);
        }

        const relationship = payorOtherGroup.get(PayorOtherFormEnum.RELATIONSHIP);
        if (relationship && !relationship.value && payer.relationship) {
            relationship.patchValue(payer.relationship);
        }

        // Patch address group
        const addressGroup = payorOtherGroup.get(PayorOtherFormEnum.ADDRESS_GROUP);
        if (addressGroup && payer.address) {
            const address = addressGroup.get(AddressFormEnum.ADDRESS);
            if (address && !address.value && payer.address.address) {
                address.patchValue(payer.address.address);
            }

            const address2 = addressGroup.get(AddressFormEnum.ADDRESS2);
            if (address2 && !address2.value && payer.address.address2) {
                address2.patchValue(payer.address.address2);
            }

            const city = addressGroup.get(AddressFormEnum.CITY);
            if (city && !city.value && payer.address.city) {
                city.patchValue(payer.address.city);
            }

            const stateCode = addressGroup.get(AddressFormEnum.STATE_CODE);
            if (stateCode && !stateCode.value && payer.address.state_code) {
                stateCode.patchValue(payer.address.state_code);
            }

            const zipCode = addressGroup.get(AddressFormEnum.ZIP_CODE);
            if (zipCode && !zipCode.value && payer.address.zip_code) {
                zipCode.patchValue(payer.address.zip_code);
            }

            // Build FULL_ADDRESS from individual fields
            this.buildFullAddressFromFields(addressGroup, payer.address);
        }
    }

    private getPrimaryPaymentMethodForm() {
        const applicationForm = this.applicationFormService.applicationForm;
        return applicationForm.get(MainSteps.PAYMENT)?.get(PaymentSubSteps.PRIMARY_PAYMENT_METHOD);
    }

    private getSecondaryPaymentMethodForm() {
        // The SECONDARY substep is conditionally attached to applicationForm based on
        // primary verification. We want to be able to patch it as soon as the data
        // arrives even before the visibility effect re-attaches it, so read from the
        // form registry (which always holds the registered instance) and fall back to
        // the application form for safety.
        const registered = this.formRegistry.getFormGroup(MainSteps.PAYMENT, PaymentSubSteps.SECONDARY_PAYMENT_METHOD);
        if (registered) return registered;
        const applicationForm = this.applicationFormService.applicationForm;
        return applicationForm.get(MainSteps.PAYMENT)?.get(PaymentSubSteps.SECONDARY_PAYMENT_METHOD);
    }

    private buildFullAddressFromFields(
        addressGroup: AbstractControl,
        addressData: {
            address?: string | null;
            address2?: string | null;
            city?: string | null;
            state_code?: string | null;
            zip_code?: string | null;
        },
    ): void {
        const parts = [addressData.address, addressData.address2, addressData.city, addressData.state_code, addressData.zip_code].filter(
            Boolean,
        );

        addressGroup.get(AddressFormEnum.FULL_ADDRESS)?.patchValue(parts.join(', '), { emitEvent: false });
    }

    private patchAchDisclaimerOnForm(): void {
        // Skip patching during save operations to prevent interference
        if (this.globalStateService.isSaving()) return;

        const appPayment = this.paymentDataService.applicationPayment();
        if (appPayment?.has_payment_disclosures === null || appPayment?.has_payment_disclosures === undefined) return;

        const achDisclaimerForm = this.formRegistry.getFormGroup(MainSteps.PAYMENT, PaymentSubSteps.ACH_DISCLAIMER);
        if (!achDisclaimerForm) return;

        const control = achDisclaimerForm.get(AchDisclaimerFormEnum.ACH_PAYMENT_DISCLAIMER_ACCEPTED);
        if (control && control.value === null) {
            // Note: emitEvent must be true for yes-no-question component styling to update
            control.patchValue(appPayment.has_payment_disclosures);
        }
    }
}
